import { Modal, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReservationVehicleCard } from "./UserPlanDetailsPage";
import { ChangeNameReqApi, SubscriptionStatus } from "./Data/ApiTransport";
import {
  SubscriptionParker,
  SubscriptionParkerChangeReq,
  Vehicle,
} from "./Data/ApiTypes";
import { useMemo, useEffect, useState } from "react";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { Hash, InfoCircleFill, Person, Envelope } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { useForm } from "react-hook-form";
import { emitSubscriptionParker } from "./Data/ApiEmit";
import { toast } from "react-toastify";
import { createApiService } from "./shared/apiConfig";
import {
  ServerRequestsAddToWaitlistRequest,
  SubscriptionApi,
  WaitlistApi,
} from "./shared/api/axios-client";
import { QrCode } from "react-bootstrap-icons";
import QrCodeModal from "./Controls/QRCodeModal";
import { useAtom } from "jotai";
import { WaitlistNoticeModal } from "./WaitlistNoticeModal";

const subscriptionApi = createApiService(SubscriptionApi);

interface Props {
  show: boolean;
  onClose: () => void;
  parker: SubscriptionParker;
  setParker: React.Dispatch<
    React.SetStateAction<SubscriptionParker | undefined>
  >;
  subStatus: SubscriptionStatus;
  onAddNew?: () => void;
  onAddExisting?: () => void;
  onRemoveVehicle?: (vehicle: Vehicle) => void;
  subId: number;
  parkers: SubscriptionParker[];
  setParkers: React.Dispatch<React.SetStateAction<SubscriptionParker[]>>;
  initialWaitlistRequestPayload: ServerRequestsAddToWaitlistRequest;
  currentSpaces: number;
  hasWaitlistEntries: boolean;
  isWaitlistEnabled: boolean;
}

const ParkerEditModal = ({
  show,
  onClose,
  parker,
  setParker,
  subStatus,
  onAddNew,
  onAddExisting,
  onRemoveVehicle,
  subId,
  parkers,
  setParkers,
  initialWaitlistRequestPayload,
  currentSpaces,
  hasWaitlistEntries,
  isWaitlistEnabled,
}: Props) => {
  const hasMoreVehiclesThanSpots = parker.vehicles?.length > parker.quantity;
  const allowedSpots = parker.quantity;
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [showQrModal, setShowQrModal] = useState(false);
  
  const initialValues = useMemo(() => emitSubscriptionParker(parker), [parker]);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistRequestPayload, setPendingWaitlistRequest] =
    useState<ServerRequestsAddToWaitlistRequest>(initialWaitlistRequestPayload);
  const [showWarning, setShowWarning] = useState(false);
  const [parkerWaitlistId, setParkerWaitlistId] = useState<number | null>(null);

  const waitlistApi = createApiService(WaitlistApi);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors },
  } = useForm<SubscriptionParker>({
    defaultValues: initialValues,
  });

  const values = watch();
  const updatedSpaces = values.quantity ?? 0;

  useEffect(() => {
    const isReducingSpaces = updatedSpaces < currentSpaces;
    const isCancel = updatedSpaces === 0;
    setShowWarning(
      (isReducingSpaces || isCancel) && hasWaitlistEntries && isWaitlistEnabled
    );
  }, [updatedSpaces, currentSpaces, hasWaitlistEntries, isWaitlistEnabled]);

  // Fetch QR code when modal loads
  useEffect(() => {
    if (show) {
      subscriptionApi
        .getParkerAccessCode({
          parkerId: parker.parker.parkerId,
          subscriptionId: subId,
        })
        .then((accessCode) => {
          const response = accessCode.data as any;
          if (response) {
            setQrCodeData(response.metaFlashRFIDNumber);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch QR code:", error);
          toast.error("Failed to load access code");
        });
    }
  }, [show]);

  useEffect(() => {
      waitlistApi
        .getWaitlistByParkerId({
            parkerId: parker.parker.parkerId,
            subscriptionId: subId
          }
        )
        .then((waitlitEntry) => {
          const waitlistEntryId = waitlitEntry.data.waitlistEntryId;
          setParkerWaitlistId(waitlistEntryId!);
        })
        .catch(() => {
          setParkerWaitlistId(null);
        });
  }, [parker]);

  const onSubmit = async (data: SubscriptionParker) => {
    let nameUpdated = false;

    const nameChanged = data.parker.name !== parker.parker.name;

    if (!data.parker.isManaged && nameChanged) {
      const nameReq: ChangeNameReqApi = {
        name: data.parker.name,
        emailAddress: data.parker.emailAddress,
      };

      try {
        const resp = await Api.changeName(nameReq);
        processResult(
          resp,
          () => {
            nameUpdated = true;
          },
          (err) => toast.error(err)
        );
      } catch (e) {
        toast.error("Failed to update name.");
      }
    }

    if (nameUpdated) {
      const updatedParker: any = {
        ...parker,
        quantity: data.quantity,
        status: data.status,
        parker: {
          ...parker.parker,
          name: data.parker.name,
          emailAddress: data.parker.emailAddress,
        },
        name: data.parker.name,
      };

      setParker(updatedParker);

      const updatedParkers = parkers.map((p) =>
        p.parker.parkerId === updatedParker.parker.parkerId ? updatedParker : p
      );

      setParkers(updatedParkers);
      reset(data);
      toast.success("Parker Updated Successfully");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage {parker.parker.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasMoreVehiclesThanSpots && (
          <div
            className="alert alert-purple my-2 d-flex align-items-center gap-2"
            style={{ backgroundColor: "var(--color-light)" }}
          >
            <InfoCircleFill />
            <div>
              You have more vehicles than spaces available. Only{" "}
              <strong>{allowedSpots}</strong> vehicle
              {allowedSpots > 1 ? "s" : ""} are permitted to be parked at a
              time.
            </div>
          </div>
        )}

        <Card className="my-2">
          <Card.Header>
            <div>Settings</div>
          </Card.Header>
          <div className="row g-3 p-4">
            {!parker.parker.isManaged && (
              <div className="col-md">
                <Input
                  icon={<Person />}
                  type="text"
                  label="Name"
                  disabled={subStatus != SubscriptionStatus.Active}
                  inputClassName="text-start"
                  {...register("parker.name", {
                    required: "Name is required",
                  })}
                  error={errors.parker?.name}
                />
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
              <Button
                className={`w-100 rounded-pill ${
                  !isDirty
                    ? "bg-secondary-subtle text-dark border-0 opacity-100"
                    : "btn-main"
                }`}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty}
              >
                Save
              </Button>
            </div>
            {showWarning && (
              <div className="alert alert-warning text-sm mb-4" role="alert">
                You’re releasing {currentSpaces - updatedSpaces} space
                {currentSpaces - updatedSpaces > 1 ? "s" : ""}. Parkers on the
                waitlist will be moved forward. If you change your mind, you’ll
                re-join the back of the waitlist.
              </div>
            )}
          </div>
        </Card>

        {qrCodeData && (
          <Card className="mt-2">
            <Card.Header>
              <div className="d-flex align-items-center gap-2">
                <QrCode />
                <span>Access QR Code</span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <Button
                  className="btn-main"
                  onClick={() => setShowQrModal(true)}
                >
                  <QrCode className="me-2" style={{ marginTop: "-3px" }} />
                  View QR Code
                </Button>
                <small className="text-muted d-block mt-2">
                  Share this with the parker to allow them to access the
                  facility
                </small>
              </div>
            </Card.Body>
          </Card>
        )}

        <Card className="mt-2">
          <Card.Header
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr max-content max-content",
            }}
          >
            <div>Vehicles</div>
            <div></div>
            <div>
              {subStatus === SubscriptionStatus.Active && (
                <>
                  <Link
                    to=""
                    onClick={(e) => {
                      e.preventDefault();
                      onAddNew?.();
                    }}
                  >
                    Add New
                  </Link>
                  <>
                    &nbsp;|&nbsp;
                    <Link
                      to=""
                      onClick={(e) => {
                        e.preventDefault();
                        onAddExisting?.();
                      }}
                    >
                      Add Existing
                    </Link>
                  </>
                </>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {parker.vehicles?.length === 0 ? (
              <div className="text-secondary">This parker has no vehicles.</div>
            ) : (
              parker.vehicles.map((v) => (
                <ReservationVehicleCard
                  key={v.vehicleId}
                  vehicle={v}
                  subscriptionStatus={subStatus}
                  onRemove={() => onRemoveVehicle?.(v)}
                />
              ))
            )}
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" className="w-100" onClick={() => onClose()}>
          Close
        </Button>
      </Modal.Footer>

      <QrCodeModal
        show={showQrModal}
        onHide={() => setShowQrModal(false)}
        value={qrCodeData}
        height={256}
      />

      <WaitlistNoticeModal
        show={showWaitlistModal}
        requestPayload={waitlistRequestPayload}
        onCancel={() => {setShowWaitlistModal(false); onClose()}}
      />
    </Modal>
  );
};

export default ParkerEditModal;
